import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Hash, Save } from "lucide-react";
import { toast } from "sonner";

const REINICIOS = [
  { id: "NUNCA", label: "Nunca" },
  { id: "ANUAL", label: "Anual" },
  { id: "MENSAL", label: "Mensal" },
];

function localPreview(c) {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  let dateseg = "";
  if (c.incluir_ano && c.incluir_mes) dateseg = yyyy + mm;
  else if (c.incluir_ano) dateseg = yyyy;
  else if (c.incluir_mes) dateseg = mm;
  const parts = [c.prefixo || "DOC"];
  if (dateseg) parts.push(dateseg);
  parts.push("1".padStart(Number(c.digitos) || 4, "0"));
  return parts.join(c.separador || "-");
}

export default function NumeracaoConfig() {
  const [configs, setConfigs] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get("/config/numeracao").then((r) => setConfigs(r.data));
  useEffect(() => { load(); }, []);

  const set = (tipo, k, v) => setConfigs((cs) => cs.map((c) => c.tipo === tipo ? { ...c, [k]: v } : c));

  const salvar = async (c) => {
    setSavingId(c.tipo);
    try {
      const { data } = await api.put(`/config/numeracao/${c.tipo}`, {
        prefixo: c.prefixo, digitos: Number(c.digitos) || 4, incluir_ano: c.incluir_ano,
        incluir_mes: c.incluir_mes, separador: c.separador || "-", reinicio: c.reinicio,
      });
      setConfigs((cs) => cs.map((x) => x.tipo === c.tipo ? data : x));
      toast.success(`Numeração de ${c.label} salva`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSavingId(null); }
  };

  return (
    <div className="space-y-5" data-testid="numeracao-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Hash className="w-8 h-8 text-primary" /> Numeração de Documentos</h1>
        <p className="text-sm text-muted-foreground mt-1">Identificação comercial por empresa. O ID interno de cada registro é imutável; alterar a numeração não renumera documentos já emitidos.</p>
      </div>

      {!configs ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid gap-4">
          {configs.map((c) => (
            <Card key={c.tipo} className="bg-card/70 p-4" data-testid={`num-${c.tipo}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-display text-lg font-bold uppercase">{c.label}</div>
                <div className="text-sm">Exemplo: <span className="font-mono font-bold text-primary" data-testid={`preview-${c.tipo}`}>{localPreview(c)}</span></div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div><Label className="text-xs">Prefixo</Label><Input value={c.prefixo} onChange={(e) => set(c.tipo, "prefixo", e.target.value)} data-testid={`prefixo-${c.tipo}`} /></div>
                <div><Label className="text-xs">Dígitos</Label><Input type="number" min={1} max={10} value={c.digitos} onChange={(e) => set(c.tipo, "digitos", e.target.value)} data-testid={`digitos-${c.tipo}`} /></div>
                <div><Label className="text-xs">Separador</Label><Input value={c.separador} onChange={(e) => set(c.tipo, "separador", e.target.value)} maxLength={3} data-testid={`sep-${c.tipo}`} /></div>
                <div>
                  <Label className="text-xs">Reinício</Label>
                  <Select value={c.reinicio} onValueChange={(v) => set(c.tipo, "reinicio", v)}>
                    <SelectTrigger data-testid={`reinicio-${c.tipo}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{REINICIOS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-6 mt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={c.incluir_ano} onCheckedChange={(v) => set(c.tipo, "incluir_ano", v)} data-testid={`ano-${c.tipo}`} /> Incluir ano</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={c.incluir_mes} onCheckedChange={(v) => set(c.tipo, "incluir_mes", v)} data-testid={`mes-${c.tipo}`} /> Incluir mês</label>
                <Button className="ml-auto" onClick={() => salvar(c)} disabled={savingId === c.tipo} data-testid={`salvar-${c.tipo}`}>
                  {savingId === c.tipo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Salvar</>}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
